import type { Account } from "@Light/components/page/account";
import { BaseQueryFn, createApi } from "@reduxjs/toolkit/query/react";
import { createContext, useContext } from "react";
import {
  AcceptRenewalPlanRequest,
  AccountUpdateRequest,
  AddressSearchResponse,
  BatteryManufacturersResponse,
  BillingAddressResponse,
  BillingInvoiceDetailsRequest,
  CancelServiceRequest,
  ComparisonInvoiceResponse,
  ComparisonInvoiceSimpleCompareResponse,
  CreatePaymentSessionResponse,
  DailyUsageDataResponse,
  DayUsageRequest,
  Device,
  DisallowedDatesResponse,
  DocumentsResponse,
  EligibilityRequest,
  EligibilityResponse,
  EnrollmentOptOutRequest,
  ESIIDSearchRequest,
  GenerateBatteryLinkRequest,
  GenerateBatteryLinkResponse,
  IdentityRequest,
  InvoiceDetailResponse,
  InvoiceResponse,
  LocationPlan,
  LocationRequest,
  MonthlyUsageDataResponse,
  MonthUsageRequest,
  OptOutFilter,
  OptOutReasonOption,
  Page,
  PayInvoiceRequest,
  PaymentMethodAddedRequest,
  PaymentMethodResponse,
  PlanRequest,
  PlanResponse,
  RateGroup,
  RenewalPlanResponse,
  SupportCallbackAvailabilityResponse,
  SupportCallbackRequest,
  SupportInfoResponse,
  TermPlans,
  TermPlansRequest,
  UpdateBillingAddressRequest,
  UsagePeriodResponse,
  Vehicle,
} from "./lightTypes";

export function createLightApi(baseQuery: BaseQueryFn) {
  const api = createApi({
    reducerPath: "lightApi",
    baseQuery,
    tagTypes: [
      "Account",
      "BillingAddress",
      "PaymentMethod",
      "BillingInvoices",
      "BillingInvoice",
      "Vehicles",
      "LocationPlans",
      "BatteryStatus",
    ],
    endpoints: (builder) => ({
      getAccount: builder.query<Account, void>({
        query: () => "",
        providesTags: ["Account"],
      }),
      updateAccount: builder.mutation<Account, AccountUpdateRequest>({
        query: (body) => ({
          url: "",
          method: "PATCH",
          body,
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              api.util.updateQueryData("getAccount", undefined, (draft) => {
                Object.assign(draft, data);
              }),
            );
          } catch {
            /* empty */
          }
        },
      }),
      searchAddress: builder.mutation<
        AddressSearchResponse[],
        string | { query: string; postal_code?: string }
      >({
        query: (args) => ({
          url: "enroll/address-search",
          method: "POST",
          body: typeof args === "string" ? { query: args } : args,
        }),
      }),
      searchPreliminaryAddress: builder.query<AddressSearchResponse[], void>({
        query: () => "enroll/preliminary-address-search",
      }),
      searchESIID: builder.mutation<AddressSearchResponse, ESIIDSearchRequest>({
        query: (body) => ({
          url: "enroll/esi-id-search",
          method: "POST",
          body,
        }),
      }),
      checkEligibility: builder.query<EligibilityResponse, EligibilityRequest>({
        query: (body) => ({
          url: "enroll/eligibility",
          method: "POST",
          body,
        }),
      }),
      requestPlans: builder.query<PlanResponse[], PlanRequest>({
        query: (body) => ({
          url: "enroll/plans/request",
          method: "POST",
          body,
        }),
      }),
      requestPlansByTerm: builder.query<TermPlans[], TermPlansRequest>({
        async queryFn({ terms, ...args }, _api, _extraOptions, baseQuery) {
          const results = await Promise.all(
            terms.map(async (term) => {
              const result = await baseQuery({
                url: "enroll/plans/request",
                method: "POST",
                body: { ...args, term_months: term },
              });
              if (result.error) {
                return { term, plans: null };
              }
              return { term, plans: result.data as PlanResponse[] };
            }),
          );
          return { data: results };
        },
      }),
      getEnrollPlan: builder.query<PlanResponse, string>({
        query: (uuid) => `enroll/plans/${encodeURIComponent(uuid)}`,
      }),
      getComparisonInvoice: builder.query<ComparisonInvoiceResponse, void>({
        query: () => "comparison-invoice",
      }),
      getComparisonInvoiceSimpleCompare: builder.query<
        ComparisonInvoiceSimpleCompareResponse,
        void
      >({
        query: () => "comparison-invoice/simple-compare",
      }),
      createPaymentSession: builder.mutation<
        CreatePaymentSessionResponse,
        void
      >({
        query: (body) => ({
          url: "/billing/payment-session",
          method: "POST",
          body,
        }),
      }),
      ensurePaymentMethodAdded: builder.mutation<
        void,
        PaymentMethodAddedRequest
      >({
        query: (body) => ({
          url: "/billing/ensure-payment-method-added",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PaymentMethod", "BillingInvoices", "BillingInvoice"],
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              api.util.updateQueryData("getAccount", undefined, (draft) => {
                draft.enrollment.has_payment_method = true;
              }),
            );
          } catch {
            /* empty */
          }
        },
      }),
      getPaymentMethod: builder.query<PaymentMethodResponse, void>({
        query: () => "/billing/payment-method",
        providesTags: ["PaymentMethod"],
      }),
      getBillingAddress: builder.query<BillingAddressResponse, void>({
        query: () => "/billing/address",
        providesTags: ["BillingAddress"],
      }),
      updateBillingAddress: builder.mutation<void, UpdateBillingAddressRequest>(
        {
          query: (body) => ({
            url: "/billing/address",
            method: "PUT",
            body,
          }),
          invalidatesTags: ["Account", "BillingAddress"],
        },
      ),
      getBillingInvoices: builder.query<Page<InvoiceResponse>, void>({
        query: () => "billing/invoices",
        providesTags: ["BillingInvoices"],
      }),
      getBillingInvoice: builder.query<
        InvoiceDetailResponse,
        BillingInvoiceDetailsRequest
      >({
        query: ({ invoiceNumber }) =>
          `billing/invoices/${encodeURIComponent(invoiceNumber)}`,
        providesTags: ["BillingInvoice"],
      }),
      payInvoice: builder.mutation<void, PayInvoiceRequest>({
        query: ({ invoiceNumber, ...body }) => ({
          url: `billing/invoices/${encodeURIComponent(invoiceNumber)}/pay`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["BillingInvoices", "BillingInvoice"],
      }),
      getLocationPlan: builder.query<LocationPlan, LocationRequest>({
        query: ({ uuid }) => `locations/${encodeURIComponent(uuid)}/plan`,
      }),
      getLocationPlans: builder.query<Page<LocationPlan>, LocationRequest>({
        query: ({ uuid }) => `locations/${encodeURIComponent(uuid)}/plans`,
        providesTags: ["LocationPlans"],
      }),
      getLocationMeterReadUsage: builder.query<
        UsagePeriodResponse[],
        LocationRequest
      >({
        query: ({ uuid }) =>
          `locations/${encodeURIComponent(uuid)}/usage/meter-reads`,
      }),
      getLocationDocuments: builder.query<DocumentsResponse, LocationRequest>({
        query: ({ uuid }) => `locations/${encodeURIComponent(uuid)}/documents`,
      }),
      getAllLocationsDocuments: builder.query<
        Page<DocumentsResponse>,
        LocationRequest
      >({
        query: ({ uuid }) =>
          `locations/${encodeURIComponent(uuid)}/all-documents`,
      }),
      cancelService: builder.mutation<void, CancelServiceRequest>({
        query: ({ location_uuid, ...body }) => ({
          url: `locations/${encodeURIComponent(location_uuid)}/cancel-service`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["Account"],
      }),
      getSupportInfo: builder.query<SupportInfoResponse, LocationRequest>({
        query: ({ uuid }) =>
          `locations/${encodeURIComponent(uuid)}/support-info`,
      }),
      updateIdentity: builder.mutation<void, IdentityRequest>({
        query: (body) => ({
          url: "enroll/identity",
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["Account"],
      }),
      finalizeEnrollment: builder.mutation<void, void>({
        query: () => ({
          url: "enroll/finalize",
          method: "POST",
        }),
        invalidatesTags: ["Account"],
      }),
      resetEnrollment: builder.mutation<void, void>({
        query: () => ({
          url: "enroll/reset",
          method: "POST",
        }),
        invalidatesTags: ["Account"],
      }),
      rateGroups: builder.query<RateGroup[], void>({
        query: () => ({
          url: "enroll/plan-groups",
          method: "GET",
        }),
      }),
      getVehicles: builder.query<Page<Vehicle>, void>({
        query: () => ({
          url: "vehicles/",
          method: "GET",
        }),
        providesTags: ["Vehicles"],
      }),
      getBatteryManufacturers: builder.query<
        BatteryManufacturersResponse,
        void
      >({
        query: () => "devices/manufacturers?type=battery",
      }),
      generateDeviceLinkUrl: builder.mutation<
        GenerateBatteryLinkResponse,
        GenerateBatteryLinkRequest
      >({
        query: (body) => ({
          url: "devices/link",
          method: "POST",
          body: {
            device_type: "BATTERY",
            redirect_link: body.redirect_link,
            manufacturer: body.manufacturer,
          },
        }),
      }),
      listDevices: builder.query<Page<Device>, { device_type?: string } | void>(
        {
          query: (params) => ({
            url: "devices",
            params,
          }),
          providesTags: ["BatteryStatus"],
        },
      ),
      getDevice: builder.query<Device, string>({
        query: (uuid) => ({
          url: `devices/${uuid}`,
          method: "GET",
        }),
        providesTags: ["BatteryStatus"],
      }),
      discoverDevices: builder.mutation<void, void>({
        query: () => ({
          url: "devices/discover",
          method: "POST",
        }),
        invalidatesTags: ["BatteryStatus"],
      }),
      supportCallbackRequest: builder.mutation<void, SupportCallbackRequest>({
        query: (body) => ({
          url: "enroll/support-callback",
          method: "POST",
          body,
        }),
      }),
      getSupportCallbackAvailability: builder.query<
        SupportCallbackAvailabilityResponse,
        void
      >({
        query: () => ({
          url: "enroll/support-callback-availability",
          method: "GET",
        }),
      }),
      getDailyUsage: builder.query<DailyUsageDataResponse, DayUsageRequest>({
        query: ({ uuid, month, year }) => {
          const path = `locations/${encodeURIComponent(uuid)}/usage/daily`;
          if (!month && !year) {
            return path;
          }

          const searchParams: { month?: string; year?: string } = {};
          if (month) {
            searchParams.month = `${month}`;
          }

          if (year) {
            searchParams.year = `${year}`;
          }

          return `${path}?${new URLSearchParams(searchParams)}`;
        },
      }),
      getMonthlyUsage: builder.query<
        MonthlyUsageDataResponse,
        MonthUsageRequest
      >({
        query: ({ uuid, year }) => {
          const path = `locations/${encodeURIComponent(uuid)}/usage/monthly`;
          if (!year) {
            return path;
          }

          const searchParams: { year?: string } = {};
          if (year) {
            searchParams.year = `${year}`;
          }

          return `${path}?${new URLSearchParams(searchParams)}`;
        },
      }),
      requestRenewalPlans: builder.mutation<
        RenewalPlanResponse[],
        LocationRequest
      >({
        query: (body) => ({
          url: `locations/${encodeURIComponent(body.uuid)}/renewal-plans/request`,
          method: "POST",
          body,
        }),
      }),
      acceptRenewalPlan: builder.mutation<void, AcceptRenewalPlanRequest>({
        query: (body) => ({
          url: `locations/${encodeURIComponent(body.location_uuid)}/renewal-plans/accept`,
          method: "POST",
          body: {
            plan_uuid: body.plan_uuid,
            service_start_date: body.service_start_date,
            terms_accepted: body.terms_accepted,
          },
        }),
        invalidatesTags: ["LocationPlans"],
      }),
      getDisallowedDates: builder.query<DisallowedDatesResponse, void>({
        query: () => ({
          url: "enroll/disallowed-dates",
          method: "GET",
        }),
      }),
      getOptOutReasons: builder.query<OptOutReasonOption[], OptOutFilter>({
        query: (filter) => ({
          url: "enroll/opt-out/reasons",
          params: { filter },
        }),
      }),
      recordEnrollmentOptOut: builder.mutation<void, EnrollmentOptOutRequest>({
        query: (body) => ({
          url: "enroll/opt-out",
          method: "POST",
          body,
        }),
      }),
    }),
  });
  return api;
}

export type LightApi = ReturnType<typeof createLightApi>;

export const LightContext = createContext<LightApi | null>(null);

export function useLight(): LightApi {
  const light = useContext(LightContext);
  if (!light) {
    throw new Error("useLight must be used inside a LightContext.Provider");
  }
  return light;
}
